import React, { useEffect, useState } from 'react';
import { Badge, Popover, Empty } from 'antd';
import JanusEnrichingMark from '../JanusEnrichingMark';
import {
  useEnrichmentQueueStore,
  useEnrichmentQueueEntriesForCurrentOwner,
} from '../../stores/useEnrichmentQueueStore';
import EnrichmentQueueRow from './EnrichmentQueueRow';

/**
 * Header icon for the enrichment queue. Renders the JanusEnrichingMark
 * (paleta clara) with an Ant badge showing pending + unviewed-completed
 * count. On click, opens a popover listing recent entries; opening the
 * popover also marks all entries as viewed so the blink calms down once
 * the user has acknowledged.
 */
const EnrichmentQueueIcon: React.FC = () => {
  const entries = useEnrichmentQueueEntriesForCurrentOwner();
  const markAllViewed = useEnrichmentQueueStore((s) => s.markAllViewed);
  const pending = entries.filter((entry) => entry.status === 'processing').length;
  const unviewedDone = entries.filter((entry) => entry.status !== 'processing' && !entry.viewed).length;
  const total = pending + unviewedDone;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open && unviewedDone > 0) {
      markAllViewed();
    }
  }, [open, unviewedDone, markAllViewed]);

  // Reidrata os envios LinkedIn em voo persistidos (fire-and-forget): o ícone
  // monta em toda tela autenticada, então um F5 no meio de um envio retoma o
  // polling aqui e o desfecho ainda chega (chip + notification + conclusão).
  useEffect(() => {
    void import('../../../modules/contacts/services/linkedinJobTracker').then((m) =>
      m.resumeLinkedinJobs(),
    );
  }, []);

  // Sempre renderizamos o ícone — antes tinha um early-return quando a
  // fila estava vazia, mas isso escondia a affordance da feature e o
  // usuário não tinha como saber que ela existe até disparar um unlock.
  // Estado vazio: ícone estático (sem animação), badge oculto, popover
  // mostra mensagem "Nenhum enrichment recente".
  const isEmpty = entries.length === 0;
  const hasBlink = unviewedDone > 0 && !open;
  const ariaLabel = pending > 0
    ? `${pending} enriquecimento(s) em andamento`
    : unviewedDone > 0
      ? `${unviewedDone} enriquecimento(s) completos`
      : 'Fila de enriquecimentos (vazia)';

  const content = (
    <div
      style={{
        width: 340,
        maxHeight: 420,
        overflowY: 'auto',
        padding: 6,
      }}
    >
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid #E2E8F0',
          marginBottom: 4,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
          Fila de enriquecimentos
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
          {pending > 0
            ? `${pending} em processamento`
            : isEmpty
              ? 'Nenhum item recente'
              : 'Nenhum em processamento'}
        </div>
      </div>
      {isEmpty ? (
        <Empty
          description="Quando você revelar um contato, ele aparece aqui."
          imageStyle={{ height: 60 }}
          style={{ padding: 16 }}
        />
      ) : (
        entries.map((e) => (
          <EnrichmentQueueRow
            key={e.messageId}
            entry={e}
            onAfterNavigate={() => setOpen(false)}
          />
        ))
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: 'hidden' }}
    >
      <Badge
        count={total}
        size="small"
        styles={{ indicator: { background: '#6366F1' } }}
      >
        <button
          aria-label={ariaLabel}
          className="flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border-0 bg-transparent"
          style={{ color: 'var(--color-text-secondary)', opacity: isEmpty ? 0.55 : 1 }}
          data-testid="enrichment-queue-icon"
        >
          <span className={hasBlink ? 'janus-queue-bell-blink' : undefined}>
            <JanusEnrichingMark
              size={20}
              variant="dark"
              animated={pending > 0}
            />
          </span>
        </button>
      </Badge>
    </Popover>
  );
};

export default EnrichmentQueueIcon;
